import API from "./api";
import { formatCurrencyAmount, getCurrencyName, normalizeOccupationCurrencyCode } from "./currencyOptions";

export function canDownloadRecommendationBiodata(recommendation) {
  return Boolean(recommendation?.id && recommendation?.to_profile_id);
}

function buildRecommendationAccessQuery(accessContext) {
  const recommendationId = accessContext?.recommendationId;
  const sourceProfileId = accessContext?.sourceProfileId;
  if (!recommendationId || !sourceProfileId) {
    return "";
  }

  const params = new URLSearchParams({
    recommendation_id: String(recommendationId),
    source_profile_id: String(sourceProfileId),
  });
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function getProfileBiodataSectionCatalog(
  profile,
  {
    lookups = {},
    includePhone = false,
    includeEmail = false,
    includeAddresses = true,
    includeRelativePhone = true,
    includeRelativeEmail = true,
  } = {}
) {
  if (!profile || typeof profile !== "object") {
    return [];
  }

  const lookupMaps = buildLookupMaps(lookups);
  return buildBiodataSections(profile, lookupMaps, {
    includePhone,
    includeEmail,
    includeAddresses,
    includeRelativePhone,
    includeRelativeEmail,
  });
}

export async function generateProfileBiodataPdf(
  profile,
  {
    lookups = {},
    includePhone = false,
    includeEmail = false,
    includeAddresses = true,
    includeRelativePhone = true,
    includeRelativeEmail = true,
    includePhoto = true,
    includedSectionIds = null,
    fileName = null,
    accessContext = null,
    output = "save",
  } = {}
) {
  if (!profile || typeof profile !== "object") {
    throw new Error("Profile data is missing.");
  }

  const [{ jsPDF }, photoAssets] = await Promise.all([
    import("jspdf"),
    includePhoto ? loadProfilePhotoAssets(profile, accessContext) : Promise.resolve([]),
  ]);

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const lookupMaps = buildLookupMaps(lookups);
  const allSections = buildBiodataSections(profile, lookupMaps, {
    includePhone,
    includeEmail,
    includeAddresses,
    includeRelativePhone,
    includeRelativeEmail,
  });
  const includedSectionIdSet = Array.isArray(includedSectionIds) && includedSectionIds.length
    ? new Set(includedSectionIds)
    : null;
  const sections = includedSectionIdSet
    ? allSections.filter((section) => includedSectionIdSet.has(section.id))
    : allSections;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const bottomMargin = 12;
  const usableWidth = pageWidth - margin * 2;
  const generatedAt = new Date().toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  let y = margin;
  let hasStartedDocument = false;

  const drawPageWatermark = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(30);
    doc.setTextColor(251, 244, 242);

    for (let watermarkY = 82; watermarkY < pageHeight - 22; watermarkY += 88) {
      doc.text("JAIN KALYANAM", 36, watermarkY, { angle: 32 });
      doc.text("JAIN KALYANAM", 126, watermarkY + 24, { angle: 32 });
    }
  };

  const drawPageChrome = () => {
    drawPageWatermark();

    doc.setDrawColor(234, 214, 209);
    doc.setLineWidth(0.5);
    doc.line(margin, 10, pageWidth - margin, 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(160, 76, 52);
    doc.text("JAIN KALYANAM", margin, 7);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(126, 126, 126);
    doc.text(`Generated ${generatedAt}`, pageWidth - margin, 7, { align: "right" });
  };

  const startNewPage = () => {
    if (hasStartedDocument) {
      doc.addPage();
    }
    hasStartedDocument = true;
    drawPageChrome();
    y = 16;
  };

  const ensureSpace = (heightNeeded) => {
    if (y + heightNeeded <= pageHeight - bottomMargin) return;
    startNewPage();
  };

  const drawTextBlock = (text, opts = {}) => {
    const {
      size = 10,
      color = [37, 41, 53],
      x = margin,
      width = usableWidth,
      lineHeight = 4.8,
      font = "normal",
    } = opts;

    const value = String(text || "").trim();
    if (!value) return;

    doc.setFont("helvetica", font);
    doc.setFontSize(size);
    doc.setTextColor(...color);

    const lines = doc.splitTextToSize(value, width);
    ensureSpace(lines.length * lineHeight + 1.5);
    doc.text(lines, x, y);
    y += lines.length * lineHeight + 1.5;
  };

  const drawSectionTitle = (title) => {
    ensureSpace(12);
    doc.setFillColor(247, 236, 232);
    doc.roundedRect(margin, y, usableWidth, 8, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(130, 58, 42);
    doc.text(String(title || "").trim(), margin + 3, y + 5.4);
    y += 10.5;
  };

  const drawBulletList = (items) => {
    safeArray(items).forEach((item) => {
      const value = String(item || "").trim();
      if (!value) return;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(37, 41, 53);

      const lines = doc.splitTextToSize(value, usableWidth - 6);
      ensureSpace(lines.length * 4.8 + 1.5);
      doc.text("-", margin, y);
      doc.text(lines, margin + 5, y);
      y += lines.length * 4.8 + 1.5;
    });
  };

  const drawKeyValueTable = (items) => {
    const rows = safeArray(items).filter((item) => item?.value || item?.value === 0);
    if (!rows.length) return;

    const labelWidth = Math.max(40, Math.min(usableWidth * 0.33, 58));
    const valueWidth = usableWidth - labelWidth;
    const rowPaddingX = 3;
    const rowPaddingY = 2.6;
    const lineHeight = 4.7;

    y += 1.5;

    rows.forEach((row, index) => {
      const labelLines = doc.splitTextToSize(String(row.label || "").trim(), labelWidth - rowPaddingX * 2);
      const valueLines = doc.splitTextToSize(String(row.value || "").trim(), valueWidth - rowPaddingX * 2);
      const contentLineCount = Math.max(labelLines.length || 1, valueLines.length || 1);
      const rowHeight = contentLineCount * lineHeight + rowPaddingY * 2;

      ensureSpace(rowHeight + 1);

      const rowTop = y;
      const fillColor = index % 2 === 0 ? [255, 255, 255] : [252, 249, 247];

      doc.setFillColor(244, 231, 226);
      doc.setDrawColor(234, 214, 209);
      doc.rect(margin, rowTop, labelWidth, rowHeight, "FD");

      doc.setFillColor(...fillColor);
      doc.rect(margin + labelWidth, rowTop, valueWidth, rowHeight, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(112, 62, 48);
      doc.text(labelLines, margin + rowPaddingX, rowTop + rowPaddingY + lineHeight - 1);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(37, 41, 53);
      doc.text(valueLines, margin + labelWidth + rowPaddingX, rowTop + rowPaddingY + lineHeight - 1);

      y += rowHeight;
    });

    y += 2.5;
  };

  const drawHero = () => {
    const primaryPhoto = photoAssets.find((asset) => asset.isPrimary) || photoAssets[0] || null;
    const photoWidth = primaryPhoto ? 44 : 0;
    const photoHeight = primaryPhoto ? 54 : 0;
    const photoX = pageWidth - margin - photoWidth;
    const textWidth = primaryPhoto ? usableWidth - photoWidth - 6 : usableWidth;
    const heroTop = y;
    const name = getProfileDisplayName(profile) || "Unnamed Profile";
    const birthDate = profile.birth_date || profile.dob;
    const location =
      profile.location_display_cache ||
      profile.location_display ||
      formatProfileLocation(profile, lookupMaps);
    const metaLine = [
      profile.gender ? labelize(profile.gender) : "",
      getAge(birthDate) != null ? `${getAge(birthDate)} yrs` : "",
      profile.marital_status_description || labelize(profile.marital_status),
      profile.height_display || (profile.height_cm ? `${profile.height_cm} cm` : ""),
    ]
      .filter(Boolean)
      .join(" | ");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(37, 41, 53);
    const nameLines = doc.splitTextToSize(name, textWidth);
    doc.text(nameLines, margin, y);
    y += nameLines.length * 8;

    if (metaLine) {
      drawTextBlock(metaLine, {
        x: margin,
        width: textWidth,
        size: 10,
        color: [95, 101, 116],
      });
    }

    if (location) {
      drawTextBlock(location, {
        x: margin,
        width: textWidth,
        size: 10,
        color: [95, 101, 116],
      });
    }

    const heroBottom = y;

    if (primaryPhoto) {
      const fitted = fitInside(primaryPhoto.width, primaryPhoto.height, photoWidth, photoHeight);
      const imageX = photoX + (photoWidth - fitted.width) / 2;
      const imageY = heroTop + (photoHeight - fitted.height) / 2;

      doc.setFillColor(251, 246, 244);
      doc.roundedRect(photoX, heroTop, photoWidth, photoHeight, 4, 4, "F");
      doc.addImage(primaryPhoto.dataUrl, "JPEG", imageX, imageY, fitted.width, fitted.height);
      y = Math.max(heroBottom, heroTop + photoHeight) + 5;
    } else {
      y += 3;
    }
  };

  const drawPhotoGallery = () => {
    if (!photoAssets.length) return;

    drawSectionTitle("Photo Gallery");

    const gap = 6;
    const cardWidth = (usableWidth - gap) / 2;
    const cardHeight = 63;

    photoAssets.forEach((asset, index) => {
      if (index % 2 === 0) {
        ensureSpace(cardHeight + 4);
      }

      const column = index % 2;
      const x = margin + column * (cardWidth + gap);
      const rowTop = y;

      doc.setFillColor(251, 246, 244);
      doc.roundedRect(x, rowTop, cardWidth, cardHeight, 3, 3, "F");

      const fitted = fitInside(asset.width, asset.height, cardWidth - 6, 50);
      const imageX = x + (cardWidth - fitted.width) / 2;
      const imageY = rowTop + 4 + (50 - fitted.height) / 2;

      doc.addImage(asset.dataUrl, "JPEG", imageX, imageY, fitted.width, fitted.height);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(108, 72, 61);
      doc.text(asset.isPrimary ? "Primary Photo" : `Photo ${index + 1}`, x + 3, rowTop + 58);

      if (column === 1 || index === photoAssets.length - 1) {
        y += cardHeight + 4;
      }
    });
  };

  const shouldDrawHero = !includedSectionIdSet || includedSectionIdSet.has("basic_details");

  startNewPage();
  if (shouldDrawHero) {
    drawHero();
  }

  sections.forEach((section) => {
    if (!section) return;
    drawSectionTitle(section.title);

    if (section.type === "paragraph") {
      drawTextBlock(section.value, { size: 10, color: [37, 41, 53] });
      return;
    }

    if (section.type === "list") {
      drawBulletList(section.items);
      return;
    }

    if (section.type === "kv") {
      drawKeyValueTable(section.items);
      return;
    }

    safeArray(section.items).forEach((item) => {
      if (!item?.value && item?.value !== 0) return;
      drawTextBlock(`${item.label}: ${item.value}`, {
        size: 10,
        color: [37, 41, 53],
      });
    });
  });

  drawPhotoGallery();
  applyFooter(doc, { margin });
  const resolvedFileName = buildBiodataFileName(profile, { fileName });

  if (output === "blob") {
    return {
      blob: doc.output("blob"),
      fileName: resolvedFileName,
    };
  }

  if (output === "arraybuffer") {
    return {
      arrayBuffer: doc.output("arraybuffer"),
      fileName: resolvedFileName,
    };
  }

  doc.save(resolvedFileName);
  return {
    fileName: resolvedFileName,
  };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function labelize(value) {
  if (!value && value !== 0) return "";
  return String(value)
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getProfileDisplayName(profile) {
  if (!profile || typeof profile !== "object") return "";
  return (
    profile.full_name ||
    [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ")
  );
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / (365.25 * 86400000));
}

function formatDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function toTime(value) {
  return value ? String(value).slice(0, 5) : "";
}

function normalizeRelationType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function buildLookupMaps(lookups) {
  return {
    locations: safeArray(lookups.locations),
    cityTiers: safeArray(lookups.cityTiers),
    castes: safeArray(lookups.castes),
    sakhas: safeArray(lookups.sakhas),
    educationCategories: safeArray(lookups.educationCategories),
    visaStatuses: safeArray(lookups.visaStatuses),
    visaNames: safeArray(lookups.visaNames),
  };
}

function getCountryName(row) {
  return String(row?.country || row?.country_name || row?.countryName || "India").trim();
}

function getCountryId(row) {
  return row?.country_id || row?.countryId || row?.countryID || null;
}

function lookupName(items, id, type = "default") {
  if (!id) return "";
  const found = safeArray(items).find((item) => Number(item.id) === Number(id));
  if (!found) return "";

  if (type === "city") {
    return [
      found.city || found.name,
      found.district || found.district_name,
      found.state || found.state_name,
      getCountryName(found),
    ]
      .filter(Boolean)
      .join(", ");
  }

  return found.name || found.display_label || "";
}

function lookupLocationDetails(items, cityId) {
  if (!cityId) return {};
  const found = safeArray(items).find((item) => Number(item.id) === Number(cityId));
  if (!found) return {};

  return {
    city_id: found.id || null,
    city: found.city || found.name || "",
    tehsil_id: found.tehsil_id || found.tehsilId || found.tehsilID || null,
    tehsil: found.tehsil || found.tehsil_name || found.tehsilName || "",
    district_id: found.district_id || found.districtId || found.districtID || null,
    district: found.district || found.district_name || found.districtName || "",
    state_id: found.state_id || found.stateId || found.stateID || null,
    state: found.state || found.state_name || found.stateName || "",
    country_id: getCountryId(found),
    country: getCountryName(found),
  };
}

function lookupHierarchyName(items, id, idKeys, nameKeys) {
  if (!id) return "";
  const found = safeArray(items).find((item) =>
    idKeys.some((key) => Number(item?.[key]) === Number(id))
  );
  if (!found) return "";
  return nameKeys.map((key) => found?.[key]).find(Boolean) || "";
}

function pushUniqueAddressPart(parts, value) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return;

  const key = text.toLowerCase();
  const exists = parts.some((part) => {
    const partKey = String(part).toLowerCase();
    return partKey === key || partKey.includes(key) || key.includes(partKey);
  });

  if (!exists) parts.push(text);
}

function cleanAddressLine(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const normalized = text.toLowerCase();
  if (
    normalized.includes("same city as source") ||
    normalized.includes("same district as source") ||
    normalized.includes("same state as source")
  ) {
    return "";
  }

  return text;
}

function formatLocationParts(parts) {
  const out = [];
  parts.forEach((value) => pushUniqueAddressPart(out, value));
  return out.join(", ");
}

function formatProfileLocation(profile, lookupMaps) {
  if (!profile) return "";
  const lookup = lookupLocationDetails(
    lookupMaps.locations,
    profile.city_id || profile.current_location_id
  );

  return formatLocationParts([
    profile.city || lookup.city,
    profile.district || lookup.district,
    profile.state || lookup.state,
    profile.country || lookup.country,
  ]);
}

function formatProfileBirthPlace(profile, lookupMaps) {
  if (!profile) return "";
  const lookup = lookupLocationDetails(
    lookupMaps.locations,
    profile.birth_place_city_id || profile.birth_place_id
  );

  return formatLocationParts([
    profile.birth_city || profile.birth_place_city || lookup.city,
    profile.birth_district || lookup.district,
    profile.birth_state || lookup.state,
    profile.birth_country || lookup.country,
  ]);
}

function formatPreferenceIds(ids, items, type = "default") {
  const names = safeArray(ids)
    .map((id) => lookupName(items, id, type))
    .filter(Boolean);
  return names.join(", ");
}

function formatAgeGapMonths(value) {
  const totalMonths = Number(value);
  if (!Number.isFinite(totalMonths)) return "";
  if (totalMonths <= 0) return "0 months";

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return [
    years ? `${years} year${years === 1 ? "" : "s"}` : "",
    months ? `${months} month${months === 1 ? "" : "s"}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function formatPreferredAgeDifference(preferences) {
  const minRaw = preferences?.preferred_age_min ?? preferences?.min_age;
  const maxRaw = preferences?.preferred_age_max ?? preferences?.max_age;
  const minMonths = Number(minRaw);
  const maxMonths = Number(maxRaw);
  const hasMin = Number.isFinite(minMonths) && minRaw !== "" && minRaw !== null && minRaw !== undefined;
  const hasMax = Number.isFinite(maxMonths) && maxRaw !== "" && maxRaw !== null && maxRaw !== undefined;

  if (hasMin || hasMax) {
    const fromLabel = `-${formatAgeGapMonths(hasMin ? minMonths : 0)}`;
    const toLabel = `+${formatAgeGapMonths(hasMax ? maxMonths : 0)}`;
    return `${fromLabel} to ${toLabel}`;
  }

  const legacyGap = preferences?.preferred_age_gap_months;
  const gapMonths = Number(legacyGap);
  if (Number.isFinite(gapMonths) && legacyGap !== "" && legacyGap !== null && legacyGap !== undefined) {
    return `-${formatAgeGapMonths(gapMonths)} to +${formatAgeGapMonths(gapMonths)}`;
  }

  return "";
}

function formatAddress(address, profile, lookupMaps) {
  const lookup = lookupLocationDetails(lookupMaps.locations, address.city_id);
  const tehsilName =
    address.tehsil_name ||
    address.tehsil ||
    lookupHierarchyName(
      lookupMaps.locations,
      address.tehsil_id,
      ["tehsil_id", "tehsilId", "tehsilID"],
      ["tehsil", "tehsil_name", "tehsilName"]
    ) ||
    (Number(address.tehsil_id) === Number(profile.tehsil_id) ? profile.tehsil : "");

  const districtName =
    address.district_name ||
    address.district ||
    lookup.district ||
    lookupHierarchyName(
      lookupMaps.locations,
      address.district_id,
      ["district_id", "districtId", "districtID"],
      ["district", "district_name", "districtName"]
    ) ||
    (Number(address.district_id) === Number(profile.district_id) ? profile.district : "");

  const stateName =
    address.state_name ||
    address.state ||
    lookup.state ||
    lookupHierarchyName(
      lookupMaps.locations,
      address.state_id,
      ["state_id", "stateId", "stateID"],
      ["state", "state_name", "stateName"]
    ) ||
    (Number(address.state_id) === Number(profile.state_id) ? profile.state : "");

  const countryName =
    address.country_name ||
    address.country ||
    lookup.country ||
    lookupHierarchyName(
      lookupMaps.locations,
      address.country_id,
      ["country_id", "countryId", "countryID"],
      ["country", "country_name", "countryName"]
    ) ||
    profile.country ||
    "India";

  const villageName =
    address.village_name ||
    address.village ||
    (Number(address.village_id) === Number(profile.village_id) ? profile.village : "");

  const parts = [];
  pushUniqueAddressPart(parts, cleanAddressLine(address.address_line1));
  pushUniqueAddressPart(parts, cleanAddressLine(address.address_line2));
  pushUniqueAddressPart(parts, villageName);
  pushUniqueAddressPart(parts, tehsilName);
  pushUniqueAddressPart(parts, districtName);
  pushUniqueAddressPart(parts, stateName);
  pushUniqueAddressPart(parts, countryName);
  pushUniqueAddressPart(parts, address.pincode);
  return parts.join(" | ");
}

function formatAddressVisa(address, lookupMaps) {
  const visaStatus = lookupName(lookupMaps.visaStatuses, address?.visa_status_id);
  const visaName = lookupName(lookupMaps.visaNames, address?.visa_name_id);
  const parts = [visaStatus, visaName].filter(Boolean);
  return parts.length ? `Visa: ${parts.join(" | ")}` : "";
}

function isServiceOccupationTypeLabel(value) {
  return /\bservice\b/i.test(String(value || "").trim());
}

function getProfileOccupations(profile) {
  const occupationRows = safeArray(profile?.occupations);
  if (occupationRows.length) return occupationRows;

  const fallback = {
    occupation_type: profile?.occupation_type || "",
    company_name: profile?.company_name || "",
    occupation_details: profile?.current_occupation_details || "",
    work_profile: profile?.work_profile || "",
    annual_income_amount: profile?.annual_income_amount ?? null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      profile?.annual_income_currency_code,
      profile?.annual_income_amount
    ),
    annual_turnover_amount: profile?.annual_turnover_amount ?? null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      profile?.annual_turnover_currency_code,
      profile?.annual_turnover_amount
    ),
    is_primary: true,
  };

  return [fallback].filter((occupation) =>
    [
      occupation.occupation_type,
      occupation.company_name,
      occupation.occupation_details,
      occupation.work_profile,
      occupation.annual_income_amount,
      occupation.annual_turnover_amount,
    ].some((value) => value !== null && value !== undefined && String(value).trim() !== "")
  );
}

function formatOccupationAmount(occupation) {
  if (!occupation) return "";

  const amount = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? occupation.annual_income_amount
    : occupation.annual_turnover_amount;

  if (amount === null || amount === undefined || amount === "") return "";

  const label = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? "Annual income"
    : "Annual turnover";
  const currencyCode = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? normalizeOccupationCurrencyCode(occupation.annual_income_currency_code, amount)
    : normalizeOccupationCurrencyCode(occupation.annual_turnover_currency_code, amount);
  const formatted = formatCurrencyAmount(amount, currencyCode || "INR");
  const currencyName = getCurrencyName(currencyCode || "INR");

  return `${label}: ${formatted}${currencyName ? ` (${currencyName})` : ""}`;
}

function showChildrenFields(maritalStatus) {
  const normalized = String(maritalStatus || "").trim().toLowerCase();
  return normalized && normalized !== "single" && normalized !== "never married";
}

function formatCoordinate(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(6) : "";
}

function formatCoordinatePair(latitude, longitude) {
  const lat = formatCoordinate(latitude);
  const lng = formatCoordinate(longitude);
  return lat && lng ? `${lat}, ${lng}` : "";
}

function immediateFamily(relatives) {
  const types = new Set(["father", "mother", "brother", "sister", "son", "daughter"]);
  return safeArray(relatives).filter((relative) =>
    types.has(normalizeRelationType(relative.relation_type))
  );
}

function extendedRelatives(relatives) {
  const types = new Set(["father", "mother", "brother", "sister", "son", "daughter"]);
  return safeArray(relatives).filter(
    (relative) => !types.has(normalizeRelationType(relative.relation_type))
  );
}

function buildBiodataSections(profile, lookupMaps, options) {
  const contacts = buildContactItems(profile, options);
  const occupations = getProfileOccupations(profile);
  const birthPlace =
    profile.birth_place ||
    profile.birth_place_display_cache ||
    formatProfileBirthPlace(profile, lookupMaps);
  const currentLocation =
    profile.location_display_cache ||
    profile.location_display ||
    formatProfileLocation(profile, lookupMaps);

  const sections = [
    {
      id: "basic_details",
      title: "Basic Details",
      type: "kv",
      items: [
        { label: "Gender", value: profile.gender ? labelize(profile.gender) : "" },
        { label: "Date of Birth", value: formatDate(profile.birth_date || profile.dob) },
        { label: "Birth Time", value: toTime(profile.birth_time) },
        { label: "Age", value: getAge(profile.birth_date || profile.dob) != null ? `${getAge(profile.birth_date || profile.dob)} years` : "" },
        { label: "Birth Place", value: birthPlace },
        { label: "Current Location", value: currentLocation },
        { label: "Birth Coordinates", value: formatCoordinatePair(profile.birth_latitude, profile.birth_longitude) },
        { label: "Height", value: profile.height_display || (profile.height_cm ? `${profile.height_cm} cm` : "") },
        { label: "Complexion", value: profile.complexion ? labelize(profile.complexion) : "" },
        { label: "Blood Group", value: profile.blood_group || "" },
        { label: "Manglik", value: profile.manglik ? labelize(profile.manglik) : "" },
        { label: "Marital Status", value: profile.marital_status_description || labelize(profile.marital_status) },
        { label: "Has Children", value: profile.has_children ? "Yes" : showChildrenFields(profile.marital_status) ? "No" : "" },
        { label: "Children Details", value: profile.marital_field_details || profile.children_description || "" },
        { label: "Sampraday", value: profile.sampraday_name || profile.sampraday || "" },
        { label: "Sampraday Details", value: profile.sampraday_details || "" },
        { label: "Caste", value: profile.caste || profile.caste_name_cache || "" },
        { label: "Sakha", value: profile.sakha || profile.sakha_name_cache || "" },
        { label: "Dadisa Sakha", value: profile.dadisa_sakha_name || "" },
        { label: "Nanisa Sakha", value: profile.nanisa_sakha_name || "" },
        { label: "Mamasa Sakha", value: profile.mamasa_sakha_name || "" },
      ],
    },
  ];

  if (profile.about_me) {
    sections.push({
      id: "about",
      title: "About",
      type: "paragraph",
      value: profile.about_me,
    });
  }

  if (contacts.length) {
    sections.push({
      id: "contact_details",
      title: "Contact Details",
      type: "kv",
      items: contacts,
    });
  }

  const educationItems = safeArray(profile.educations)
    .map((education, index) => ({
      label: education.degree_name || education.education_description || `Education ${index + 1}`,
      value: [
        education.education_category,
        education.institution_name,
        education.education_location,
        education.passing_year,
        String(education.education_status || "").toLowerCase() === "pursuing" ? "Pursuing" : "",
        education.is_highest_degree ? "Highest degree" : "",
      ]
        .filter(Boolean)
        .join(" | "),
    }))
    .filter((item) => item.label || item.value);

  if (educationItems.length) {
    sections.push({
      id: "education",
      title: "Education",
      type: "kv",
      items: educationItems,
    });
  }

  const occupationItems = occupations
    .map((occupation, index) => ({
      label: occupation.occupation_details || occupation.occupation_type || `Occupation ${index + 1}`,
      value: [
        occupation.is_primary ? "Primary" : "",
        occupation.occupation_type,
        occupation.company_name,
        occupation.work_profile,
        formatOccupationAmount(occupation),
      ]
        .filter(Boolean)
        .join(" | "),
    }))
    .filter((item) => item.label || item.value);

  if (occupationItems.length) {
    sections.push({
      id: "career",
      title: "Career",
      type: "kv",
      items: occupationItems,
    });
  }

  const immediateFamilyItems = immediateFamily(profile.relatives)
    .map((relative) => formatRelativeRow(relative, options))
    .filter((item) => item.label || item.value);
  if (immediateFamilyItems.length) {
    sections.push({
      id: "immediate_family",
      title: "Immediate Family",
      type: "kv",
      items: immediateFamilyItems,
    });
  }

  const extendedFamilyItems = extendedRelatives(profile.relatives)
    .map((relative) => formatRelativeRow(relative, options))
    .filter((item) => item.label || item.value);
  if (extendedFamilyItems.length) {
    sections.push({
      id: "extended_family",
      title: "Extended Family",
      type: "kv",
      items: extendedFamilyItems,
    });
  }

  if (options?.includeAddresses !== false) {
    const addressItems = safeArray(profile.addresses)
      .map((address) => ({
        label: labelize(address.address_type || "Address"),
        value: [
          formatAddress(address, profile, lookupMaps),
          formatAddressVisa(address, lookupMaps),
        ]
          .filter(Boolean)
          .join(" | "),
      }))
      .filter((item) => item.label || item.value);

    if (addressItems.length) {
      sections.push({
        id: "addresses",
        title: "Addresses",
        type: "kv",
        items: addressItems,
      });
    }
  }

  if (profile.preferences) {
    sections.push({
      id: "partner_preferences",
      title: "Partner Preferences",
      type: "kv",
      items: [
        {
          label: "Preferred Age Difference",
          value:
            formatPreferredAgeDifference(profile.preferences) ||
            (
              profile.preferences.preferred_age_min && profile.preferences.preferred_age_max
                ? `${profile.preferences.preferred_age_min} - ${profile.preferences.preferred_age_max} years`
                : ""
            ),
        },
        {
          label: "Preferred Occupations",
          value: safeArray(profile.preferences.preferred_occupation_types).join(", "),
        },
        {
          label: "Preferred City Tiers",
          value:
            formatPreferenceIds(profile.preferences.preferred_city_tier_ids, lookupMaps.cityTiers) ||
            formatPreferenceIds(
              profile.preferences.preferred_city_ids,
              lookupMaps.locations,
              "city"
            ),
        },
        {
          label: "Preferred Castes",
          value: formatPreferenceIds(profile.preferences.preferred_caste_ids, lookupMaps.castes),
        },
        {
          label: "Non Preferred Sakhas",
          value: formatPreferenceIds(profile.preferences.non_preferred_sakha_ids, lookupMaps.sakhas),
        },
        {
          label: "Preferred Education",
          value: formatPreferenceIds(
            profile.preferences.preferred_education_category_ids,
            lookupMaps.educationCategories
          ),
        },
        {
          label: "Notes",
          value: profile.preferences.notes || "",
        },
      ],
    });
  }

  return sections;
}

function buildContactItems(profile, options) {
  const includePhone = Boolean(options?.includePhone);
  const includeEmail = Boolean(options?.includeEmail);

  return safeArray(profile?.contacts)
    .filter((contact) => {
      const type = String(contact?.contact_type || "").trim().toLowerCase();
      if (!type) return false;
      if (type === "email") return includeEmail;
      return includePhone;
    })
    .map((contact) => ({
      label: labelize(contact.contact_type || "Contact"),
      value: contact.contact_value || "",
    }))
    .filter((item) => item.value);
}

function formatRelativeRow(relative, options = {}) {
  const name =
    relative.full_name ||
    [relative.first_name, relative.middle_name, relative.last_name].filter(Boolean).join(" ") ||
    "Unnamed";

  const headline = [
    labelize(relative.relation_type),
    relative.family_side && relative.family_side !== "none"
      ? labelize(relative.family_side)
      : "",
    relative.sakha_name || "",
  ]
    .filter(Boolean)
    .join(" | ");

  const normalizedRelationType = String(relative?.relation_type || "").trim().toLowerCase();
  const normalizedGender = String(relative?.gender || "").trim().toLowerCase();
  const partnerLabel = normalizedRelationType === "sister" || (normalizedRelationType === "sibling" && normalizedGender === "female")
    ? "Husband Name"
    : "Spouse Name";

  const details = [
    [
      relative.occupation_type || "",
      relative.occupation_details || "",
      relative.company_name || "",
      relative.work_profile || "",
    ]
      .filter(Boolean)
      .join(" | "),
    relative.marital_status ? labelize(relative.marital_status) : "",
    String(relative.marital_status || "").trim().toLowerCase() === "married" && relative.partner_name ? `${partnerLabel}: ${relative.partner_name}` : "",
    [
      options?.includeRelativePhone === false ? null : relative.phone,
      options?.includeRelativeEmail === false ? null : relative.email,
    ]
      .filter(Boolean)
      .join(" | "),
    safeArray(relative.education)
      .map((education) =>
        [education.degree_name, education.education_description]
          .filter(Boolean)
          .join(" | ")
      )
      .filter(Boolean)
      .join("; "),
  ]
    .filter(Boolean)
    .join(" | ");

  return {
    label: name,
    value: [headline, details].filter(Boolean).join(" | "),
  };
}

function extractPhotoMedia(profile, accessContext = null) {
  const profileId = profile?.id || profile?.profile_id;
  const accessQuery = buildRecommendationAccessQuery(accessContext);
  const rawMedia = safeArray(profile?.media || profile?.files)
    .filter((item) => {
      const mediaType = String(item?.media_type || "photo").trim().toLowerCase();
      return mediaType === "photo" && (item?.file_url || item?.url || item?.src);
    })
    .map((item) => {
      const fileUrl = String(item?.file_url || item?.url || item?.src || "").trim();
      return {
        ...item,
        file_url: fileUrl,
        candidate_urls: Array.from(
          new Set(
            [
              profileId && item?.id
                ? `/api/profiles/${profileId}/images/${item.id}/content${accessQuery}`
                : "",
              fileUrl,
            ].filter(Boolean)
          )
        ),
      };
    });

  const primaryFileUrl = String(profile?.primary_photo_url_cache || "").trim();
  const hasMatchingPrimaryMedia = rawMedia.some(
    (item) => String(item?.file_url || "").trim() === primaryFileUrl
  );
  const effectivePrimaryMedia =
    rawMedia.find((item) => Boolean(item?.is_primary)) ||
    rawMedia.find((item) => String(item?.file_url || "").trim() === primaryFileUrl) ||
    rawMedia[0] ||
    null;

  const media = rawMedia.map((item) => {
    const isEffectivePrimary =
      effectivePrimaryMedia &&
      (
        (item?.id != null && effectivePrimaryMedia?.id != null && String(item.id) === String(effectivePrimaryMedia.id)) ||
        String(item?.file_url || "").trim() === String(effectivePrimaryMedia?.file_url || "").trim()
      );

    if (!isEffectivePrimary || !profileId) return item;

    return {
      ...item,
      is_primary: true,
        candidate_urls: Array.from(
          new Set(
            [
              `/api/profiles/${profileId}/primary-photo/content${accessQuery}`,
              ...(Array.isArray(item.candidate_urls) ? item.candidate_urls : []),
            ].filter(Boolean)
          )
      ),
    };
  });

  if (primaryFileUrl && !hasMatchingPrimaryMedia) {
    media.unshift({
      id: "primary-photo-cache",
      file_url: primaryFileUrl,
      media_type: "photo",
      is_primary: true,
      candidate_urls: [
        profileId ? `/api/profiles/${profileId}/primary-photo/content${accessQuery}` : "",
        primaryFileUrl,
      ].filter(Boolean),
    });
  } else if (!media.length && primaryFileUrl) {
    media.unshift({
      id: "primary-photo-cache",
      file_url: primaryFileUrl,
      media_type: "photo",
      is_primary: true,
      candidate_urls: [
        profileId ? `/api/profiles/${profileId}/primary-photo/content${accessQuery}` : "",
        primaryFileUrl,
      ].filter(Boolean),
    });
  }

  return media.map((item, index) => ({
    ...item,
    is_primary: Boolean(item?.is_primary) || index === 0,
  }));
}

async function loadProfilePhotoAssets(profile, accessContext = null) {
  const media = extractPhotoMedia(profile, accessContext);
  const assets = await Promise.all(
    media.map(async (item, index) => {
      try {
        const asset = await fetchImageAsPdfAsset(
          Array.isArray(item?.candidate_urls) && item.candidate_urls.length
            ? item.candidate_urls
            : [item?.file_url]
        );
        if (!asset) return null;
        return {
          ...asset,
          isPrimary: Boolean(item.is_primary) || index === 0,
        };
      } catch (error) {
        return null;
      }
    })
  );

  return assets.filter(Boolean);
}

async function fetchImageAsPdfAsset(urlOrUrls) {
  const urls = Array.isArray(urlOrUrls) ? urlOrUrls : [urlOrUrls];

  let lastError = null;
  for (const candidate of urls) {
    const url = String(candidate || "").trim();
    if (!url) continue;

    try {
      const blob = await fetchImageBlob(url);
      return convertBlobToJpegAsset(blob);
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  return null;
}

async function fetchImageBlob(url) {
  if (url.startsWith("/api/")) {
    const response = await API.get(url.replace(/^\/api/, ""), {
      responseType: "blob",
    });
    return response.data;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load image: ${response.status}`);
  }
  return response.blob();
}

async function convertBlobToJpegAsset(blob) {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(blob);
    try {
      const scale = Math.min(1, 1400 / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(bitmap, 0, 0, width, height);
      return {
        dataUrl: canvas.toDataURL("image/jpeg", 0.84),
        width,
        height,
      };
    } finally {
      if (typeof bitmap.close === "function") {
        bitmap.close();
      }
    }
  }

  const dataUrl = await blobToDataUrl(blob);
  const image = await loadImage(dataUrl);
  const scale = Math.min(1, 1400 / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);
  return {
    dataUrl: canvas.toDataURL("image/jpeg", 0.84),
    width,
    height,
  };
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to decode image"));
    image.src = src;
  });
}

function fitInside(sourceWidth, sourceHeight, maxWidth, maxHeight) {
  if (!sourceWidth || !sourceHeight) {
    return { width: maxWidth, height: maxHeight };
  }

  const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
  return {
    width: sourceWidth * ratio,
    height: sourceHeight * ratio,
  };
}

function applyFooter(doc, { margin }) {
  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(126, 126, 126);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin, pageHeight - 5, {
      align: "right",
    });
  }
}

function sanitizeFileNamePart(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]+/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function buildBiodataFileName(profile, options = {}) {
  if (options?.fileName) {
    return String(options.fileName).trim() || "biodata.pdf";
  }

  const name = sanitizeFileNamePart(getProfileDisplayName(profile)) || "PROFILE";
  return `JKM_PROFILE_${name}_BIO_DATA.pdf`;
}
